<script setup>
import { computed, ref, watch } from 'vue';
import { CsTeleport } from '../teleport';
import { CsTransitionRoot } from '../transition';
import CsDrawerBackdrop from './CsDrawerBackdrop.vue';
import CsDrawerPanel from './CsDrawerPanel.vue';
import { DrawerProvider } from './useDrawer';

/**
 * @typedef {object} DrawerProps
 * @property {boolean} [isOpen=false] - Controls whether the drawer is displayed
 * @property {boolean} [isShowCloseIcon=true] - Controls visibility of the close icon
 * @property {boolean} [isCloseOnBackdropClick=true] - Whether clicking the backdrop closes the drawer
 * @property {boolean} [isCloseOnEscape=true] - Whether pressing Escape key closes the drawer
 * @property {('sm'|'md'|'lg'|'xl')} [size='md'] - Size of the drawer
 * @property {number} [zIndex=1000] - zIndex of the drawer
 */

/**
 * @type {DrawerProps}
 */
const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  isShowCloseIcon: {
    type: Boolean,
    default: true,
  },
  isCloseOnBackdropClick: {
    type: Boolean,
    default: true,
  },
  isCloseOnEscape: {
    type: Boolean,
    default: true,
  },
  size: {
    type: String,
    default: 'md', // sm, lg, full
  },
  zIndex: {
    type: Number,
    default: 1000,
  },
});

const emit = defineEmits(['close']);

const hasDrawerHeader = ref(false);
const hasDrawerFooter = ref(false);

function onClose() {
  emit('close');
}

function onBackdropClick() {
  if (props.isCloseOnBackdropClick) {
    onClose();
  }
}

function onEscape(event) {
  if (event.key === 'Escape') {
    onClose();
  }
}

function registerEventListeners() {
  if (props.isCloseOnEscape) {
    window.addEventListener('keydown', onEscape);
  }
}

function unregisterEventListeners() {
  if (props.isCloseOnEscape) {
    window.removeEventListener('keydown', onEscape);
  }
}

function enableBodyScrollLock() {
  document.body.style.overflow = 'hidden';
}

function disableBodyScrollLock() {
  document.body.style.overflow = 'auto';
}

// Provide the drawer context
DrawerProvider({
  onClose,
  onBackdropClick,
  rootProps: computed(() => props),
  hasDrawerHeader,
  hasDrawerFooter,
});

// Register event listeners when the drawer is opened
watch(
  () => props.isOpen,
  newValue => {
    if (newValue) {
      enableBodyScrollLock();
      registerEventListeners();
    } else {
      disableBodyScrollLock();
      unregisterEventListeners();
    }
  }
);
</script>

<template>
  <CsTeleport>
    <CsTransitionRoot
      :is-open="isOpen"
      data-component="CsDrawer"
      data-slot="root"
      role="dialog"
      :style="{
        '--modal-z-index': props.zIndex,
      }"
      class="relative z-[var(--modal-z-index)]"
    >
      <CsDrawerBackdrop />

      <CsDrawerPanel :size="props.size">
        <slot />
      </CsDrawerPanel>
    </CsTransitionRoot>
  </CsTeleport>
</template>
